import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class SquareSettingsCalculated extends ModelObject<SquareSettingsCalculated> {

    /**
    * The Square merchant id. Used to uniquely identify this integration.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public merchantId: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.merchantId = jsonSerializedObject.merchantId;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.merchantId = this.merchantId;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class SquareOutletMappingReadOnly extends ModelObject<SquareOutletMappingReadOnly> {

    /**
    * The square link settings which this mapping belongs to.
    */
    public squareSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.squareSettingsId = Guid.parseOrEmpty(jsonSerializedObject.squareSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.squareSettingsId = this.squareSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "squareSettingsId", rule: (v: any) => !v.isEmpty() || "SquareOutletMappingReadOnly.SquareSettingsId cannot be empty"},
    ];
}


export class SquareOutletMapping extends ModelObject<SquareOutletMapping> {

    /**
    * The square location name.
    * Not Nullable
    */
    public squareLocationName: string = "";
    /**
    * The Brewman outlet id to use for orders at the given square location.
    */
    public brewmanOutletId: Guid = Guid.createEmpty();
    public readOnly: SquareOutletMappingReadOnly = new SquareOutletMappingReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.squareLocationName = jsonSerializedObject.squareLocationName;
          this.brewmanOutletId = Guid.parseOrEmpty(jsonSerializedObject.brewmanOutletId);
          this.readOnly = new SquareOutletMappingReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.squareLocationName = this.squareLocationName;
        toRet.brewmanOutletId = this.brewmanOutletId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "brewmanOutletId", rule: (v: any) => !v.isEmpty() || "SquareOutletMapping.BrewmanOutletId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class SquareSettings extends ModelObject<SquareSettings> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    private _defaultDeliveryStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for delivery charge of imported orders. This will be used if no BrewMan product name matches the Shipping Line Title in Square
    */
    public get defaultDeliveryStockItemId(): Guid | null { return this._defaultDeliveryStockItemId }
    public set defaultDeliveryStockItemId(value: Guid | null) {
      this._defaultDeliveryStockItemId = value === undefined ? null : value;
    }
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * If provided then Orders on Square from before this Date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    private _outletId: Guid | null = null;
    /**
    * Outlet Id if all square orders import to a single outlet. If null then OutletMappings should be configured to map specific square locations to Outlets.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    private _priceListId: Guid | null = null;
    /**
    * PriceList Id. If provided then Products on Square will be updated with the List Price in BrewMan.
    */
    public get priceListId(): Guid | null { return this._priceListId }
    public set priceListId(value: Guid | null) {
      this._priceListId = value === undefined ? null : value;
    }
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    private _defaultGratuityStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for gratuity of imported orders. 
    */
    public get defaultGratuityStockItemId(): Guid | null { return this._defaultGratuityStockItemId }
    public set defaultGratuityStockItemId(value: Guid | null) {
      this._defaultGratuityStockItemId = value === undefined ? null : value;
    }
    private _miscellaneousProductId: Guid | null = null;
    /**
    * Identifies the product to be used for line items that cannot be mapped to Brewman.
    */
    public get miscellaneousProductId(): Guid | null { return this._miscellaneousProductId }
    public set miscellaneousProductId(value: Guid | null) {
      this._miscellaneousProductId = value === undefined ? null : value;
    }
    public calculated: SquareSettingsCalculated = new SquareSettingsCalculated();
    public outletMappings: (SquareOutletMapping)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.defaultDeliveryStockItemId = jsonSerializedObject.defaultDeliveryStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultDeliveryStockItemId);
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.priceListId = jsonSerializedObject.priceListId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.priceListId);
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.defaultGratuityStockItemId = jsonSerializedObject.defaultGratuityStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultGratuityStockItemId);
          this.miscellaneousProductId = jsonSerializedObject.miscellaneousProductId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.miscellaneousProductId);
          this.calculated = new SquareSettingsCalculated(jsonSerializedObject.calculated);
          this.outletMappings = jsonSerializedObject.outletMappings.map((v: any) => new SquareOutletMapping(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.defaultDeliveryStockItemId = this.defaultDeliveryStockItemId == null ? null : this.defaultDeliveryStockItemId.toString();
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.priceListId = this.priceListId == null ? null : this.priceListId.toString();
        toRet.runInBackground = this.runInBackground;
        toRet.defaultGratuityStockItemId = this.defaultGratuityStockItemId == null ? null : this.defaultGratuityStockItemId.toString();
        toRet.miscellaneousProductId = this.miscellaneousProductId == null ? null : this.miscellaneousProductId.toString();
        toRet.calculated = this.calculated.toJsonSerializedObject();
        toRet.outletMappings = this.outletMappings.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "SquareSettings.Id cannot be empty"},
            {field: "defaultDeliveryStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "SquareSettings.DefaultDeliveryStockItemId cannot be empty"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "SquareSettings.OutletId cannot be empty"},
            {field: "priceListId", rule: (v: any) => v == null || !v.isEmpty() || "SquareSettings.PriceListId cannot be empty"},
            {field: "defaultGratuityStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "SquareSettings.DefaultGratuityStockItemId cannot be empty"},
            {field: "miscellaneousProductId", rule: (v: any) => v == null || !v.isEmpty() || "SquareSettings.MiscellaneousProductId cannot be empty"},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
            {field: "outletMappings", rule: (v: any) => !!v || "outletMappings is required"},
            {field: "outletMappings", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SquareOutletMapping[]).reduce((acc: Array<string>, curr: SquareOutletMapping) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class TenantSettingsId extends ModelObject<TenantSettingsId> {

    public tenantId: Guid = Guid.createEmpty();
    public squareSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.squareSettingsId = Guid.parseOrEmpty(jsonSerializedObject.squareSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.squareSettingsId = this.squareSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.TenantId cannot be empty"},
            {field: "squareSettingsId", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.SquareSettingsId cannot be empty"},
    ];
}


export class SquareSettingsService extends ServiceBase {

  public async isSquareAuthenticated(squareSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<boolean> {
    const req = {
    tenantId:api.tenantId.toString(),
    squareSettingsId:squareSettingsId.toString(),
    };
    const extractor = (response:any) => response.authenticated;;
    return this.makeJsonRequest("squareSettings/v1/isSquareAuthenticated", req, false, extractor, opts);
  };

  public async removeSquareAuthentication(squareSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    squareSettingsId:squareSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("squareSettings/v1/removeSquareAuthentication", req, true, extractor, opts);
  };

  public async getSquareSettings(squareSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SquareSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    squareSettingsId:squareSettingsId.toString(),
    };
    const extractor = (response:any) => new SquareSettings(response.squareSettings);;
    return this.makeJsonRequest("squareSettings/v1/getSquareSettings", req, false, extractor, opts);
  };

  public async getAllSquareSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SquareSettings)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.squareSettings.map((v: any) => new SquareSettings(v));;
    return this.makeJsonRequest("squareSettings/v1/getAllSquareSettings", req, false, extractor, opts);
  };

  public async getSquareSettingsForMerchantId(merchantId: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SquareSettings | null> {
    const req = {
    tenantId:api.tenantId.toString(),
    merchantId:merchantId,
    };
    const extractor = (response:any) => response.squareSettings == null ? null : new SquareSettings(response.squareSettings);;
    return this.makeJsonRequest("squareSettings/v1/getSquareSettingsForMerchantId", req, false, extractor, opts);
  };

  public async saveSquareSettings(squareSettings: SquareSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    squareSettings:squareSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("squareSettings/v1/saveSquareSettings", req, true, extractor, opts);
  };

  public async hardDeleteSquareSettings(squareSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    squareSettingsId:squareSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("squareSettings/v1/hardDeleteSquareSettings", req, true, extractor, opts);
  };

  public async createSquareLoginUrl(squareSettingsId: Guid, returnPageUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:api.tenantId.toString(),
    squareSettingsId:squareSettingsId.toString(),
    returnPageUrl:returnPageUrl,
    };
    const extractor = (response:any) => response.url;;
    return this.makeJsonRequest("squareSettings/v1/createSquareLoginUrl", req, false, extractor, opts);
  };

  public async completeSquareAuthentication(squareSettingsId: Guid, queryString: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    squareSettingsId:squareSettingsId.toString(),
    queryString:queryString,
    };
    const extractor = null;
    return this.makeJsonRequest("squareSettings/v1/completeSquareAuthentication", req, true, extractor, opts);
  };
}

export const squareSettingsService = new SquareSettingsService();